import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CEFR_LEVELS,
  clearTokens,
  fetchSupportedLocalePairs,
  fetchUserProfile,
  updatePreferredLocale,
  updateStudiedLanguages,
} from './api';
import { localeForLanguage } from './i18n';
import { usePreferredLocale } from './PreferredLocaleContext';
import ThemeToggle from './ThemeToggle';
import LanguageSwitcher from './LanguageSwitcher';
import './ChatList.css';
import './Profile.css';

// The account profile page. It gathers everything that used to live in the
// sidebar popup — UI language, theme, saved-login deletion — plus the languages
// the user is studying and their CEFR level for each, and what is left of their
// AI allowance.
//
// `onLogout` is optional and only supplied for an authenticated session: deleting
// the saved login ends the session, so the button only appears when there is one.
function Profile({ onLogout }) {
  const { t } = useTranslation();
  const { setPreferredLocale } = usePreferredLocale();

  // The languages the user studies, as [{ languageCode, level }] (StudiedLanguageDto).
  const [studied, setStudied] = useState([]);
  // The language codes that can be studied (distinct persona languages the backend
  // supports), from which the "add a language" picker is built.
  const [studyOptions, setStudyOptions] = useState([]);
  // The AI spend and the allowance it counts against (#208), kept as the backend reports
  // them so the row can show what is left of the budget.
  const [totalAiCost, setTotalAiCost] = useState(0);
  const [spendingAllowance, setSpendingAllowance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [profile, pairs] = await Promise.all([
          fetchUserProfile(),
          fetchSupportedLocalePairs(),
        ]);
        if (cancelled) return;

        setStudied(profile.studiedLanguages || []);
        setTotalAiCost(profile.totalAiCost || 0);
        setSpendingAllowance(profile.spendingAllowance || 0);

        const seen = new Set();
        const languages = [];
        for (const pair of pairs) {
          const code = pair.personaLocale.languageCode;
          if (!seen.has(code)) {
            seen.add(code);
            languages.push(code);
          }
        }
        setStudyOptions(languages);
      } catch (err) {
        if (!cancelled) setError(t('profile.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [t]);

  // Persists the whole studied-language set. Updates optimistically and reconciles
  // with the server's reply, rolling back on failure so the UI never claims a save
  // that did not happen.
  const persist = useCallback(
    async (next, previous) => {
      setSaving(true);
      setError('');
      setStudied(next);
      try {
        const profile = await updateStudiedLanguages(next);
        setStudied(profile.studiedLanguages || []);
      } catch (err) {
        setStudied(previous);
        setError(t('profile.saveError'));
      } finally {
        setSaving(false);
      }
    },
    [t],
  );

  const handleAdd = (code) => {
    if (!code || studied.some((s) => s.languageCode === code)) return;
    persist([...studied, { languageCode: code, level: CEFR_LEVELS[0] }], studied);
  };

  const handleRemove = (code) =>
    persist(studied.filter((s) => s.languageCode !== code), studied);

  const handleLevelChange = (code, level) =>
    persist(
      studied.map((s) => (s.languageCode === code ? { ...s, level } : s)),
      studied,
    );

  // Mirrors the chosen UI language to the user's profile (best-effort): the switch
  // itself has already changed i18next + localStorage.
  const handleLanguageChange = (language) => {
    const locale = localeForLanguage(language);
    setPreferredLocale(locale);
    updatePreferredLocale(locale).catch(() => {
      // Non-fatal: the language still changed locally; only the server copy failed.
    });
  };

  const handleDeleteTokens = () => {
    clearTokens();
    if (onLogout) onLogout();
  };

  const languageLabel = (code) => t(`languages.${code}`, { defaultValue: code });

  const available = studyOptions.filter(
    (code) => !studied.some((s) => s.languageCode === code),
  );

  // What is left to spend. The cap is a pre-flight check rather than a hard ceiling, so the
  // request that trips it still runs and can push the spend past the allowance (#206): report
  // that overshoot as nothing left, since a negative budget is not a thing the user can act on.
  const remainingBudget = Math.max(0, spendingAllowance - totalAiCost);

  return (
    <div className="chat-list-container form-page-container">
      <div className="chat-list-header form-page-header">
        <h2>{t('profile.title')}</h2>
      </div>

      <div className="chat-list-content settings-page">
        <div className="settings-row">
          <span className="settings-row-label">{t('profile.language')}</span>
          <LanguageSwitcher onChange={handleLanguageChange} />
        </div>

        <div className="settings-row">
          <span className="settings-row-label">{t('profile.theme')}</span>
          <ThemeToggle />
        </div>

        {/* The allowance, what it has been spent on, and what that leaves — in that order,
            so the three rows read as the subtraction they are. */}
        <div className="settings-row">
          <span className="settings-row-label">{t('profile.aiAllowance')}</span>
          <span className="settings-row-value">${spendingAllowance.toFixed(3)}</span>
        </div>

        <div className="settings-row">
          <span className="settings-row-label">{t('profile.totalAiCost')}</span>
          <span className="settings-row-value">${totalAiCost.toFixed(3)}</span>
        </div>

        <div className="settings-row">
          <span className="settings-row-label">{t('profile.remainingAiAllowance')}</span>
          <span className="settings-row-value">${remainingBudget.toFixed(3)}</span>
        </div>

        <div className="settings-section">
          <h3 className="settings-section-title">{t('profile.studiedLanguages')}</h3>
          <p className="settings-section-help">{t('profile.studiedLanguagesHelp')}</p>

          {loading ? (
            <div className="settings-status">{t('profile.loading')}</div>
          ) : (
            <>
              {studied.length === 0 && (
                <p className="settings-empty">{t('profile.noStudiedLanguages')}</p>
              )}

              {studied.length > 0 && (
                <ul className="studied-language-list">
                  {studied.map((s) => (
                    <li key={s.languageCode} className="studied-language-row">
                      <div className="studied-language-head">
                        <span className="studied-language-name">
                          {languageLabel(s.languageCode)}
                        </span>
                        <button
                          type="button"
                          className="studied-language-remove"
                          onClick={() => handleRemove(s.languageCode)}
                          disabled={saving}
                          aria-label={t('profile.removeLanguage')}
                        >
                          ×
                        </button>
                      </div>
                      <div
                        className="cefr-levels"
                        role="group"
                        aria-label={t('profile.level')}
                      >
                        {CEFR_LEVELS.map((level) => (
                          <button
                            type="button"
                            key={level}
                            className={`cefr-level${level === s.level ? ' is-active' : ''}`}
                            onClick={() => handleLevelChange(s.languageCode, level)}
                            disabled={saving}
                            aria-pressed={level === s.level}
                          >
                            {level}
                          </button>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {available.length > 0 && (
                <select
                  className="settings-add-language"
                  value=""
                  onChange={(e) => handleAdd(e.target.value)}
                  disabled={saving}
                  aria-label={t('profile.addLanguage')}
                >
                  <option value="">{t('profile.addLanguage')}</option>
                  {available.map((code) => (
                    <option key={code} value={code}>
                      {languageLabel(code)}
                    </option>
                  ))}
                </select>
              )}
            </>
          )}

          {error && <div className="error-message">{error}</div>}
        </div>

        {onLogout && (
          <div className="settings-row">
            <span className="settings-row-label">{t('profile.savedLogin')}</span>
            <button
              type="button"
              className="settings-delete-tokens"
              onClick={handleDeleteTokens}
            >
              {t('profile.deleteSavedLogin')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Profile;
